'use client'

import { useEffect, useRef, useState } from 'react'
import { useCustomerListStoreCompat } from '../model/use-customer-list-machine'
import type { Customer } from '../model/types'

// Module-level dedupe: avoids two `interaction-card` and `interaction-
// settings-panel` instances both firing `crm.customer.get?id=X` in the
// same render cycle. Promises stay until resolution, then GC'd.
const inFlight = new Map<string, Promise<Customer | null>>()

/**
 * Customer-by-id resolver with on-demand fetch.
 *
 * - First reads from the customer-list XState store. If the row was
 *   already loaded by `useCrmSync` we return it immediately and never
 *   hit the network.
 * - If the id is set but missing from the store (workspace route
 *   doesn't pre-load customers), fires a single `crm.customer.get`
 *   via CrmApiService, dedupes concurrent calls per-id, and pushes the
 *   mapped row into the store so siblings can read it from the store.
 *
 * Used by `interaction-card` and `interaction-settings-panel` to fix
 * the "Name: —" / unknown-customer leak on the workspace route.
 */
export function useCustomerById(id: string | undefined | null): {
  customer: Customer | null
  isLoading: boolean
} {
  const { customers, addCustomer } = useCustomerListStoreCompat()

  // Read fresh from the store on every render (reactive to store updates).
  const fromStore = id ? customers.find((c) => c.id === id) ?? null : null

  const [, force] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const triedRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!id) return
    if (fromStore) return
    if (triedRef.current.has(id)) return
    triedRef.current.add(id)

    let cancelled = false

    const resolveCustomer = async (): Promise<Customer | null> => {
      // Lazy-import to avoid pulling @repo/services into the module's
      // critical bundle path; this code only runs when a customer is
      // actually missing from the store.
      const { ServiceRegistry, CrmApiService } = await import('@repo/services')
      try {
        const mainService = ServiceRegistry.getMainService()
        const crmApi = new CrmApiService(mainService)
        const result = await crmApi.customerGet({ id })
        if (!result) return null
        return mapApiCustomerLite(result as Record<string, unknown>)
      } catch (error) {
        console.warn('[useCustomerById] fetch failed', id, error)
        return null
      }
    }

    let promise = inFlight.get(id)
    if (!promise) {
      promise = resolveCustomer()
      inFlight.set(id, promise)
      promise.finally(() => inFlight.delete(id))
    }

    setIsLoading(true)
    promise
      .then((c) => {
        if (cancelled) return
        if (c) {
          addCustomer(c)
        }
        // Force a render so the consumer re-reads from the store. The
        // store update above also re-renders subscribers, but if the
        // result was null we still want to clear the loading flag.
        force((n) => n + 1)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
    // Effect intentionally depends only on the id and on whether the
    // row exists in the store. `addCustomer` is referentially
    // unstable; reading it inside the closure is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, fromStore?.id])

  return { customer: fromStore, isLoading }
}

/**
 * Minimal Swift-customer → UI-customer mapper.
 *
 * `mapApiToCustomer` in `apps/web/src/shared/lib/use-crm-api.ts` is the
 * authoritative version (handles legacy nexus-shell `identifiers[]` as
 * well as Swift's flat shape). For workspace fetches we hit the
 * Swift-canonical `crm.customer.get` directly via CrmApiService and
 * therefore only need the canonical-fields branch.
 */
function mapApiCustomerLite(data: Record<string, unknown>): Customer {
  const str = (k: string) => (typeof data[k] === 'string' ? (data[k] as string) : undefined)
  const num = (k: string) => (typeof data[k] === 'number' ? (data[k] as number) : undefined)

  const tags = Array.isArray(data['tags']) ? (data['tags'] as string[]) : []
  const displayName = str('displayName') ?? str('name') ?? ''
  const createdAt = str('createdAt') ?? new Date().toISOString()
  const updatedAt = str('updatedAt') ?? createdAt

  return {
    id: String(data['id'] ?? ''),
    displayName,
    email: str('email'),
    phone: str('phone'),
    companyId: str('companyId'),
    language: str('language'),
    timezone: str('timezone'),
    note: str('note'),
    segment: str('segment'),
    score: num('score'),
    tags,
    interactionCount: num('interactionCount') ?? 0,
    createdAt,
    updatedAt,
    // Legacy back-compat — see `entities/customer/model/types.ts`.
    name: displayName,
    type: 'individual',
    status: 'active',
    contacts: [],
    totalRevenue: 0,
    currency: '',
    lifetimeValue: 0,
  }
}
